import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
    ArrowLeft,
    BedDouble,
    Briefcase,
    Coffee,
    Dumbbell,
    House,
    Plane,
    ShoppingBag,
    Tag,
    Trees,
    Utensils,
    Wine,
    type LucideIcon,
} from 'lucide-react'
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps'
import {
    Checkin,
    CollectionItemWithCollection,
    getCheckins,
} from '../../api/client'
import CheckinItem from '../CheckinItem'
import { StandardPin } from './pinStyle'

/** Mirror of the picker's CATEGORY_ICON map so the detail header can show
 *  the right icon next to the category name. Slug-keyed — unknown slugs
 *  fall through to a generic Tag. */
const CATEGORY_ICON: Record<string, LucideIcon> = {
    home: House,
    work: Briefcase,
    restaurant: Utensils,
    cafe: Coffee,
    bar: Wine,
    gym: Dumbbell,
    park: Trees,
    shop: ShoppingBag,
    hotel: BedDouble,
    travel: Plane,
    other: Tag,
}

/**
 * Sibling of LocationViewerModal aimed at read-only consumers (timeline chip
 * tap today; will also drive the per-place detail view in the upcoming
 * places-collection UI). Differs from the viewer in two ways: (1) no Edit
 * affordance — this is for surfaces where there's no inline editing context;
 * (2) when the location is a saved Place (carries a `collectionItemId`),
 * surfaces the most recent checkins for that place plus a "View all" link.
 *
 * Coords-only invocations still work: the modal collapses to a header + map
 * + footer, no recent-checkins block. That keeps the chip-tap on a bare pin
 * usable.
 *
 * Two exports:
 *  - `LocationDetailModal`: standalone modal (backdrop + view). Used by
 *    chip clicks and the places-collection page.
 *  - `LocationDetailView`: same content without the backdrop. Used by
 *    `CheckinLocationsModal` so the overview map and the detail view can
 *    live inside a single modal instance — the user moves between them
 *    without ever seeing two modals stacked.
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY as string | undefined
const DETAIL_MAP_ID = 'memgenie-location-detail'
const DETAIL_DEFAULT_ZOOM = 15
const RECENT_LIMIT = 5

export interface LocationDetailValue {
    /** Place collection item id. When set, the recent-checkins block and the
     *  "View all checkins" link render. Bare-coords callers omit this. */
    collectionItemId?: number
    lat?: number
    lng?: number
    name?: string | null
    address?: string | null
    googlePlaceId?: string | null
    /** Category name (e.g. "Restaurant") and slug (e.g. "restaurant") for
     *  the saved Place. Both null/undefined for bare-coords and for Places
     *  that never got categorized. Slug drives the icon; name drives the
     *  label. */
    categoryName?: string | null
    categorySlug?: string | null
    /** Freeform user context (e.g. "Coachella 2026") that tells similar places
     *  apart. Displayed for any saved place; editable when `onSaveNotes` is
     *  wired (places-collection view). */
    notes?: string | null
}

interface LocationDetailViewProps {
    value: LocationDetailValue
    /** When set, the header shows a back arrow that calls this. The
     *  unified `CheckinLocationsModal` uses this to return to the overview
     *  map without unmounting. */
    onBack?: () => void
    onEdit?: () => void
    onClose: () => void
}

/**
 * Card content (no backdrop). Owns its own data-loading for recent
 * checkins. Re-fetches when `value.collectionItemId` changes — important
 * when this view is reused inside a stateful parent that swaps values
 * without unmounting.
 */
export const LocationDetailView: React.FC<LocationDetailViewProps> = ({
    value,
    onBack,
    onEdit,
    onClose,
}) => {
    const [recent, setRecent] = useState<Checkin[]>([])
    const [recentCollectionItems, setRecentCollectionItems] = useState<
        Record<string, CollectionItemWithCollection>
    >({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!value.collectionItemId) {
            setRecent([])
            setRecentCollectionItems({})
            setError(null)
            return
        }
        const collectionItemId = value.collectionItemId
        let cancelled = false
        setLoading(true)
        setError(null)
        getCheckins(undefined, undefined, RECENT_LIMIT, undefined, 'most_recent', undefined, undefined, collectionItemId)
            .then((response) => {
                if (cancelled) return
                setRecent(response.checkins)
                setRecentCollectionItems(response.collection_items || {})
            })
            .catch((err) => {
                if (cancelled) return
                console.error('Failed to load recent checkins for place:', err)
                setError('Failed to load recent checkins.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => { cancelled = true }
    }, [value.collectionItemId])

    const hasCoords = value.lat != null && value.lng != null
    const subtitle =
        value.address ||
        (value.name
            ? null
            : hasCoords
                ? `${value.lat!.toFixed(5)}°, ${value.lng!.toFixed(5)}°`
                : null)

    return (
        <div
            className="bg-white rounded-lg shadow-xl flex flex-col w-[720px] max-w-[95vw] max-h-[90vh] overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <div className="min-w-0 flex-1 pr-4 flex items-center gap-2">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="text-gray-500 hover:text-gray-800 shrink-0 -ml-1"
                            aria-label="Back to overview"
                            title="Back to overview"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                    )}
                    <div className="min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                            {value.name || 'Pinned location'}
                        </h3>
                        {subtitle && (
                            <div className="text-xs text-gray-500 truncate">{subtitle}</div>
                        )}
                        {value.categoryName && (() => {
                            const Icon = value.categorySlug
                                ? (CATEGORY_ICON[value.categorySlug] ?? Tag)
                                : Tag
                            return (
                                <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                                    <Icon className="w-3 h-3" />
                                    <span>{value.categoryName}</span>
                                </div>
                            )
                        })()}
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 shrink-0"
                    aria-label="Close"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className="flex-1 overflow-y-auto">
                {hasCoords && GOOGLE_MAPS_API_KEY && (
                    <div className="h-[280px] flex-shrink-0">
                        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                            <Map
                                style={{ width: '100%', height: '100%' }}
                                defaultCenter={{ lat: value.lat!, lng: value.lng! }}
                                defaultZoom={DETAIL_DEFAULT_ZOOM}
                                mapId={DETAIL_MAP_ID}
                                gestureHandling="greedy"
                                disableDefaultUI={false}
                            >
                                <AdvancedMarker position={{ lat: value.lat!, lng: value.lng! }}>
                                    <StandardPin />
                                </AdvancedMarker>
                            </Map>
                        </APIProvider>
                    </div>
                )}
                {hasCoords && !GOOGLE_MAPS_API_KEY && (
                    <div className="px-4 py-3 text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                        Map unavailable — VITE_GOOGLE_MAPS_BROWSER_KEY is not set.
                    </div>
                )}

                {value.collectionItemId != null && value.notes && (
                    <div className="px-4 pt-4">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                            Notes
                        </h4>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{value.notes}</p>
                    </div>
                )}

                {value.collectionItemId != null && (
                    <div className="px-4 py-4">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                            Recent checkins
                        </h4>
                        {loading ? (
                            <div className="flex justify-center py-6">
                                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500" />
                            </div>
                        ) : error ? (
                            <p className="text-sm text-rose-500">{error}</p>
                        ) : recent.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No checkins yet for this place.</p>
                        ) : (
                            <div>
                                {recent.map((c, idx) => (
                                    <CheckinItem
                                        key={c.id}
                                        checkin={c}
                                        index={idx}
                                        showSeparator={idx > 0}
                                        editingCheckinId={null}
                                        showNewCheckinComposer={false}
                                        features={[]}
                                        recentFeatures={[]}
                                        sortOrder="most_recent"
                                        savingState={null}
                                        showMenu={false}
                                        showMap={false}
                                        collectionItems={recentCollectionItems}
                                        formatDate={(d) => new Date(d).toLocaleDateString()}
                                        getComposerCheckinFromCheckin={() => ({ body: '', applies_to: '', features: [], projects: [] })}
                                        handleFieldEdit={() => {}}
                                        handleDeleteCheckin={() => {}}
                                        handleEditCheckin={() => {}}
                                        handleDuplicateCheckin={() => {}}
                                        handleFilterSelect={() => {}}
                                        handleReanalyzeCheckin={() => {}}
                                        setEditingCheckinId={() => {}}
                                        setCheckins={() => {}}
                                        setShowNewCheckinComposer={() => {}}
                                        firstElementIsEmptyCheckin={() => false}
                                        isSelectionMode={false}
                                        isSelected={false}
                                        onToggleSelection={() => {}}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="mt-3">
                            <Link
                                to={`/checkins?collection_item=${value.collectionItemId}`}
                                onClick={onClose}
                                className="text-sm text-amber-800 hover:text-amber-900"
                            >
                                View all checkins →
                            </Link>
                        </div>
                    </div>
                )}
            </div>

            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2 flex-shrink-0">
                {onEdit && (
                    <button
                        onClick={onEdit}
                        className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                    >
                        Edit
                    </button>
                )}
                <button
                    onClick={onClose}
                    className="px-3 py-1.5 text-sm bg-action text-plum rounded hover:bg-action-dark"
                >
                    Close
                </button>
            </div>
        </div>
    )
}

interface LocationDetailModalProps {
    open: boolean
    value: LocationDetailValue | null
    /** Optional Edit affordance. When set, an Edit button renders in the
     *  footer; tapping calls this and the caller decides whether to swap
     *  this modal out for LocationPickerModal (places-collection view) or
     *  ignore (chip-tap surface stays read-only). */
    onEdit?: () => void
    onClose: () => void
}

const LocationDetailModal: React.FC<LocationDetailModalProps> = ({
    open,
    value,
    onEdit,
    onClose,
}) => {
    if (!open || !value) return null
    return (
        <Backdrop onClose={onClose}>
            <LocationDetailView value={value} onEdit={onEdit} onClose={onClose} />
        </Backdrop>
    )
}

const Backdrop: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({
    onClose,
    children,
}) => (
    <div
        className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onClose}
    >
        {children}
    </div>
)

export default LocationDetailModal
